/**
 * Consultas e gravações de Saúde pelo TanStack Query.
 *
 * Papel: aqui ficam `usePlanosSaude` e `useMedicos`, que mantêm em cache o que
 * a tela lê, e as quatro mutações que gravam e mandam a listagem
 * correspondente se atualizar.
 *
 * Como usar: toda chave deste módulo começa por `CHAVE_SAUDE`, e cada mutação
 * invalida apenas o ramo que alterou — gravar um médico não tem por que fazer
 * as apólices serem buscadas de novo.
 *
 * `usePlanosSaude` é lido também pela tela inicial, que anuncia o que está
 * vencendo. Salvar uma apólice com validade nova acende ou apaga o aviso sem
 * que ninguém precise recarregar a página, porque a invalidação alcança a mesma
 * entrada do cache.
 */
import { type NovoContatoMedico, type NovoPlanoSaude } from '@concierge/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { atualizarMedico, buscarMedicos, criarMedico, excluirMedico } from '../api/medicos';
import { atualizarPlano, buscarPlanos, criarPlano, excluirPlano } from '../api/planos-saude';

/** Prefixo de todas as chaves de cache deste módulo. */
export const CHAVE_SAUDE = ['saude'] as const;

/** Chave da lista de apólices. */
export const CHAVE_PLANOS = [...CHAVE_SAUDE, 'planos'] as const;

/** Chave da agenda de médicos. */
export const CHAVE_MEDICOS = [...CHAVE_SAUDE, 'medicos'] as const;

/**
 * Lê as apólices cadastradas.
 *
 * O interruptor existe para a tela inicial, que monta o aviso de vencimento sem
 * saber de antemão quais módulos a pessoa tem: hook não se chama
 * condicionalmente, e a consulta feita por quem não tem `saude` voltaria 403 a
 * cada abertura. Com `habilitado` falso, o hook é chamado e a requisição não
 * sai.
 *
 * @param habilitado - Se a consulta deve mesmo ir à API. Padrão verdadeiro.
 * @returns Resultado da consulta, com a pessoa aninhada em cada apólice.
 */
export function usePlanosSaude(habilitado = true) {
  return useQuery({
    queryKey: CHAVE_PLANOS,
    queryFn: ({ signal }) => buscarPlanos(signal),
    enabled: habilitado,
  });
}

/**
 * Lê a agenda de médicos.
 *
 * @returns Resultado da consulta, em ordem alfabética de nome.
 */
export function useMedicos() {
  return useQuery({
    queryKey: CHAVE_MEDICOS,
    queryFn: ({ signal }) => buscarMedicos(signal),
  });
}

/** Variáveis de `useSalvarPlano`. */
interface SalvarPlanoVariaveis {
  /** Identificador da apólice a alterar, ou `null` para criar uma nova. */
  id: string | null;
  dados: NovoPlanoSaude;
}

/**
 * Cria ou altera uma apólice, conforme venha identificador.
 *
 * O `mutateAsync` rejeita com `ErroDaApi` quando a API recusa — 409 significa
 * que aquela pessoa já tem aquela apólice naquela operadora.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto a gravação não responde.
 */
export function useSalvarPlano() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dados }: SalvarPlanoVariaveis) =>
      id === null ? criarPlano(dados) : atualizarPlano(id, dados),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_PLANOS }),
  });
}

/**
 * Apaga uma apólice.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto a exclusão não responde.
 */
export function useExcluirPlano() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => excluirPlano(id),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_PLANOS }),
  });
}

/** Variáveis de `useSalvarMedico`. */
interface SalvarMedicoVariaveis {
  /** Identificador do médico a alterar, ou `null` para criar um novo. */
  id: string | null;
  dados: NovoContatoMedico;
}

/**
 * Cria ou altera um médico da agenda, conforme venha identificador.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto a gravação não responde.
 */
export function useSalvarMedico() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dados }: SalvarMedicoVariaveis) =>
      id === null ? criarMedico(dados) : atualizarMedico(id, dados),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_MEDICOS }),
  });
}

/**
 * Apaga um médico da agenda.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto a exclusão não responde.
 */
export function useExcluirMedico() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => excluirMedico(id),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_MEDICOS }),
  });
}
